import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { ARTWORKS } from "@/data/artworks";

const FILTERS = ["Engineering/Manufacturing", "Fun", "Photography", "Gold"];

const filterTestId = (f) => `gallery-filter-${f.toLowerCase().replace(/[^a-z]/g, "-").replace(/-+/g, "-")}`;

const ArtworkCard = ({ art, onOpen }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], art.loose ? ["0%", "0%"] : ["-7%", "7%"]);

  return (
    <motion.figure
      ref={ref}
      data-testid={`gallery-item-${art.id}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8%" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className={`group cursor-pointer ${art.span}`}
      onClick={() => onOpen(art)}
    >
      <div className={`relative overflow-hidden border border-black/10 ${art.aspect}`}>
        <motion.img
          src={art.url}
          alt={art.title}
          style={{ y }}
          loading="lazy"
          className={
            art.loose
              ? "h-full w-full object-cover transition-[transform,filter] duration-700 ease-out group-hover:brightness-105"
              : "h-[114%] w-full scale-[1.12] object-cover transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.18] group-hover:brightness-105"
          }
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        {art.meta && (
          <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-200 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
            {art.meta}
          </span>
        )}
        {art.year && (
          <span className="absolute right-4 top-4 font-mono text-[10px] tracking-[0.25em] text-[#D4AF37]">
            {art.year}
          </span>
        )}
      </div>
      <figcaption className="mt-4 flex items-baseline justify-between">
        {art.title && (
          <span className="font-display text-2xl font-light italic text-zinc-800 transition-colors group-hover:text-[#9A7B24]">
            {art.title}
          </span>
        )}
        <span className="ml-auto shrink-0 pl-4 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{art.category}</span>
      </figcaption>
    </motion.figure>
  );
};

export const GalleryGrid = () => {
  const [filter, setFilter] = useState("Engineering/Manufacturing");
  const [active, setActive] = useState(null);
  const visible = ARTWORKS.filter((a) => a.category === filter);

  const step = (dir) => {
    const i = visible.findIndex((a) => a.id === active.id);
    setActive(visible[(i + dir + visible.length) % visible.length]);
  };

  return (
    <section id="archive" className="px-6 py-24 lg:px-16 lg:py-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#9A7B24]">The Archive</p>
          <h2 className="font-display mt-4 text-3xl font-light tracking-tight text-[#131311] sm:text-4xl lg:text-5xl">
            Selected Works
          </h2>
        </div>
        <div className="flex flex-wrap gap-5">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={filterTestId(f)}
              onClick={() => setFilter(f)}
              className={`font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
                filter === f ? "text-[#9A7B24]" : "text-zinc-400 hover:text-zinc-600"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </motion.div>

      <div className="grid grid-cols-12 gap-x-6 gap-y-14 lg:gap-x-10 lg:gap-y-24">
        {visible.map((art) => (
          <ArtworkCard key={art.id} art={art} onOpen={setActive} />
        ))}
      </div>
      {visible.length === 0 && (
        <p data-testid="gallery-empty" className="py-20 text-center font-mono text-[11px] uppercase tracking-[0.35em] text-zinc-400">
          Pieces coming soon
        </p>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/95 p-6 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="lightbox-close-btn"
              onClick={() => setActive(null)}
              className="absolute right-6 top-6 text-zinc-400 transition-colors hover:text-[#D4AF37]"
            >
              <X size={26} />
            </button>
            <button
              data-testid="lightbox-prev-btn"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-zinc-400 transition-colors hover:text-[#D4AF37] lg:left-10"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              data-testid="lightbox-next-btn"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-zinc-400 transition-colors hover:text-[#D4AF37] lg:right-10"
            >
              <ChevronRight size={32} />
            </button>
            <motion.div
              key={active.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.url} alt={active.title} className="max-h-[78vh] w-auto max-w-full object-contain" />
              <div className="mt-5 flex items-baseline justify-between gap-6">
                <span className="font-display text-2xl font-light italic text-zinc-100">{active.title}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                  {active.meta} · {active.year}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
